import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuthStore } from '@/store/useAuthStore';
import { useCurrentUser } from '@/services/user';
import { Loader2 } from 'lucide-react';
import type { Role } from '@/types/auth';

interface ProtectedRouteProps {
    children: ReactNode;
    requiredRole?: Role | Role[];
    fallbackUrl?: string;
}

export default function ProtectedRoute({
    children,
    requiredRole,
    fallbackUrl = '/login',
}: ProtectedRouteProps) {
    const location = useLocation();
    const { isAuthenticated, isLoading, user } = useAuthStore();

    // Fetch user data if authenticated but no user data
    const { isLoading: isUserLoading } = useCurrentUser();

    // Show loading while checking authentication
    if (isLoading || (isAuthenticated && isUserLoading && !user)) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="flex items-center space-x-2">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span>Loading...</span>
                </div>
            </div>
        );
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated || !user) {
        return <Navigate to={fallbackUrl} state={{ from: location }} replace />;
    }

    // Check role requirements if specified
    if (requiredRole) {
        const requiredRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];

        if (!requiredRoles.includes(user.role)) {
            // Redirect to unauthorized page or dashboard
            return <Navigate to="/unauthorized" replace />;
        }
    }
    return <>{children}</>;
}

// Convenience components for specific roles
export function AdminRoute({ children }: { children: ReactNode }) {
    return <ProtectedRoute requiredRole={['ADMIN', 'SUPER_ADMIN']}>{children}</ProtectedRoute>;
}

export function SuperAdminRoute({ children }: { children: ReactNode }) {
    return <ProtectedRoute requiredRole="SUPER_ADMIN">{children}</ProtectedRoute>;
}
